import { Button, Combobox, DatePicker, Dialog, EmptyState, FilterToolbar, notify, Select, StatusPill, Toggle, useConfirm, type FilterItem, type PillTone } from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { CARRIERS, carrierName } from "../freight/Shipments";
import { FunnelChart, OnTimeChart, RevenueTrendChart, SlaChart } from "./ReportCharts";
import { useReportViews } from "./ReportViews";
import {
  apiQuery,
  describeParams,
  describeSchedule,
  fmtCell,
  GROUP_LABEL,
  paramsFromUrl,
  QUEUES,
  RANGE_LABEL,
  RANGE_PRESETS,
  ROLE_LABEL,
  urlFromParams,
  type Overview,
  type RangePreset,
  type ReportData,
  type ReportGroup,
  type ReportMeta,
  type ReportParams,
  type SavedView,
  type Schedule,
  type SchedulesResponse,
} from "./reportsApi";

export type { ReportData } from "./reportsApi";
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const STATE_TONE: Record<Schedule["state"], PillTone> = { active: "success", paused: "neutral", waiting_for_email: "warning", failing: "danger" };
const when = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Insights > Reports: operational charts, the reports, saved views and scheduled emails. */
export function ReportsPage() {
  const list = useQuery({ queryKey: ["console", "reports"], queryFn: () => api<{ reports: ReportMeta[] }>("/v1/console/reports") });
  const overview = useQuery({ queryKey: ["console", "reports", "overview"], queryFn: () => api<Overview>("/v1/console/reports/overview") });
  const views = useQuery({ queryKey: ["console", "report-views", "all"], queryFn: () => api<{ views: SavedView[] }>("/v1/console/report-views") });
  const o = overview.data;
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Reports</h1>
          <p className="ffa-head__sub">From FlexFreight&apos;s own quotes, shipments and tickets. Eastern business days. Every report exports to CSV.</p>
        </div>
      </header>
      {list.isError ? <div className="ffa-notice" role="alert"><span>{message(list.error)}</span></div> : null}

      <section aria-labelledby="ops-h" className="ffa-reportdash">
        <div className="ffa-card__headrow">
          <h2 id="ops-h" className="ffa-section__title">Operations at a glance</h2>
          {o ? <span className="ffa-small ffa-muted">Last 90 days ({o.window.from} to {o.window.to}, Eastern) unless noted</span> : null}
        </div>
        {overview.isError ? (
          <div className="ffa-notice" role="alert"><span>Couldn&apos;t load the charts. {message(overview.error)}</span><Button size="sm" onClick={() => void overview.refetch()}>Try again</Button></div>
        ) : !o ? (
          <p className="ffa-muted" aria-busy="true">Loading charts…</p>
        ) : (
          <div className="ffa-dash__grid">
            <section className="ffx-card ffa-card ffa-dash__trend" aria-labelledby="rt-h">
              <div className="ffa-card__headrow">
                <h3 id="rt-h" className="ffa-card__title">Revenue{o.showsCost ? " and margin" : ""} trend</h3>
                <Link className="ffa-small" to="/reports/revenue?range=last_12_months">Last 12 months · open report</Link>
              </div>
              <RevenueTrendChart data={{ ...o.revenueTrend, showsCost: o.showsCost }} />
            </section>
            <section className="ffx-card ffa-card" aria-labelledby="fn-h">
              <div className="ffa-card__headrow">
                <h3 id="fn-h" className="ffa-card__title">Quote conversion</h3>
                <Link className="ffa-small" to="/reports/conversion?range=last_90_days">Open report</Link>
              </div>
              <FunnelChart steps={o.funnel.steps} />
            </section>
            <section className="ffx-card ffa-card" aria-labelledby="ot-h">
              <div className="ffa-card__headrow">
                <h3 id="ot-h" className="ffa-card__title">Carrier on-time delivery</h3>
                <Link className="ffa-small" to="/reports/carriers?range=last_90_days">Open report</Link>
              </div>
              {o.carrierOnTime.length ? <OnTimeChart carriers={o.carrierOnTime} /> : <p className="ffa-small ffa-muted">No booked shipments in the last 90 days.</p>}
            </section>
            <section className="ffx-card ffa-card" aria-labelledby="sla-h">
              <div className="ffa-card__headrow">
                <h3 id="sla-h" className="ffa-card__title">Ticket SLA</h3>
                <Link className="ffa-small" to="/reports/support?range=last_90_days">Open report</Link>
              </div>
              <SlaChart sla={o.ticketSla} />
            </section>
            <section className="ffx-card ffa-card ffa-dash__wide" aria-labelledby="na-h">
              <h3 id="na-h" className="ffa-card__title">Not measured yet</h3>
              <ul className="ffa-nolist">
                {o.unavailable.map((u) => (
                  <li key={u.metric}><span className="ffa-cell-strong">{u.metric}</span> <span className="ffa-nodata">No data source</span> <span className="ffa-small ffa-muted">{u.reason.replace(/^No data source: /, "")}</span></li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </section>

      <section aria-labelledby="all-h" className="ffa-reportdash">
        <h2 id="all-h" className="ffa-section__title">All reports</h2>
        <ul className="ffa-reportgrid">
          {(list.data?.reports ?? []).map((r) => (
            <li key={r.id} className="ffx-card ffa-card">
              {r.status === "ready" ? <Link className="ffa-rowlink ffa-card__title" to={`/reports/${r.id}`}>{r.title}</Link> : <span className="ffa-card__title">{r.title}</span>}
              <p className="ffa-small ffa-muted">{r.description}</p>
              {r.status === "integration_required" ? <StatusPill tone="neutral">Integration required</StatusPill> : null}
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="sv-h" className="ffa-reportdash">
        <h2 id="sv-h" className="ffa-section__title">Saved views</h2>
        {views.isError ? (
          <p className="ffa-small ffa-muted">{message(views.error)}</p>
        ) : (views.data?.views.length ?? 0) === 0 ? (
          <p className="ffa-small ffa-muted">{views.isLoading ? "Loading…" : "None yet. Open a report, set it up, and choose View > Save current view."}</p>
        ) : (
          <ul className="ffa-viewlist ffx-card ffa-card">
            {views.data!.views.map((v) => (
              <li key={v.id} className="ffa-viewlist__row">
                <span className="ffa-viewlist__text">
                  <Link className="ffa-rowlink ffa-cell-strong" to={`/reports/${v.reportId}?${urlFromParams(v.config, v.id)}`}>{v.name}</Link>
                  <span className="ffa-small ffa-muted">{v.reportTitle} · {describeParams(v.config, carrierName)}</span>
                </span>
                <span className="ffa-small ffa-muted">{v.visibility === "role" ? `Shared with ${ROLE_LABEL[v.sharedRole!]}` : "Only me"}{v.schedules ? ` · emailed (${v.schedules})` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SchedulesPanel />
    </section>
  );
}

/** Scheduled report emails. Owners and admins see (and can pause) everyone's; others see their own. */
function SchedulesPanel({ viewId, compact = false }: { viewId?: string; compact?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["console", "report-schedules", viewId ?? "all"], queryFn: () => api<SchedulesResponse>(`/v1/console/report-schedules${viewId ? `?view=${viewId}` : ""}`) });
  const { confirm, dialog } = useConfirm();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["console", "report-schedules"] });
  const pause = useMutation({
    mutationFn: (s: { id: string; paused: boolean }) => api<{ schedule: Schedule }>(`/v1/console/report-schedules/${s.id}`, { method: "PATCH", body: { paused: s.paused } }),
    onSuccess: (r) => {
      refresh();
      notify.success(r.schedule.state === "paused" ? "Schedule paused" : "Schedule resumed");
    },
    onError: (e) => notify.error("Not changed", message(e)),
  });
  const remove = async (s: Schedule) => {
    if (!(await confirm({ title: "Stop emailing this report?", body: `"${s.viewName ?? "This view"}" will no longer be sent to ${s.recipients.length} recipient${s.recipients.length === 1 ? "" : "s"}. The saved view stays.`, confirmLabel: "Delete schedule", danger: true }))) return;
    try {
      await api(`/v1/console/report-schedules/${s.id}`, { method: "DELETE" });
      refresh();
      void qc.invalidateQueries({ queryKey: ["console", "report-views"] });
      notify.success("Schedule deleted");
    } catch (e) {
      notify.error("Not deleted", message(e));
    }
  };
  const d = q.data;
  if (compact && (!d || d.schedules.length === 0)) return null;
  return (
    <section aria-labelledby={`sch-h-${viewId ?? "all"}`} className="ffa-reportdash">
      <div className="ffa-card__headrow">
        <h2 id={`sch-h-${viewId ?? "all"}`} className={compact ? "ffa-card__title" : "ffa-section__title"}>{compact ? "Emailed on a schedule" : "Scheduled emails"}</h2>
        {d && !compact ? <span className="ffa-small ffa-muted">{d.scope === "all" ? "Everyone's schedules (owners and admins can pause any)" : "Schedules you set up or can see"}</span> : null}
      </div>
      {d && !d.emailConnected && d.schedules.length ? (
        <div className="ffa-notice" role="status"><StatusPill tone="warning">Waiting for email to be connected</StatusPill><span className="ffa-small">Nothing is sent until customer email (Resend) is connected in <Link to="/admin/integrations">Integrations</Link>.</span></div>
      ) : null}
      {q.isError ? (
        <p className="ffa-small ffa-muted">{message(q.error)}</p>
      ) : !d ? (
        <p className="ffa-small ffa-muted">Loading…</p>
      ) : d.schedules.length === 0 ? (
        <p className="ffa-small ffa-muted">No reports are emailed yet. Open a saved view and choose View &gt; Email this report.</p>
      ) : (
        <div className="ffx-table-wrap ffa-tablewrap">
          <table className="ffx-table ffa-table ffa-schedtable">
            <caption className="ffx-sr">Scheduled report emails</caption>
            <thead>
              <tr>
                {compact ? null : <th scope="col">View</th>}
                <th scope="col">When</th>
                <th scope="col">Sends</th>
                <th scope="col">To</th>
                <th scope="col">Status</th>
                <th scope="col">Next</th>
                <th scope="col"><span className="ffx-sr">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {d.schedules.map((s) => (
                <tr key={s.id}>
                  {compact ? null : (
                    <td data-label="View">
                      {s.reportId ? <Link className="ffa-rowlink ffa-cell-strong" to={`/reports/${s.reportId}?view=${s.viewId}`}>{s.viewName}</Link> : s.viewName}
                      {s.createdBy.isMe ? null : <span className="ffa-small ffa-muted"> · by {s.createdBy.name ?? "someone"}</span>}
                    </td>
                  )}
                  <td data-label="When">{describeSchedule(s)}</td>
                  <td data-label="Sends">{s.delivery === "csv" ? "CSV + link" : "Link"}</td>
                  <td data-label="To"><span className="ffa-trunc" title={s.recipients.join(", ")}>{s.recipients.length === 1 ? s.recipients[0] : `${s.recipients.length} staff`}</span></td>
                  <td data-label="Status">
                    <StatusPill tone={STATE_TONE[s.state]}>{s.stateLabel}</StatusPill>
                    {s.state === "failing" && s.lastError ? <span className="ffa-small ffa-muted"> {s.lastError}</span> : null}
                  </td>
                  <td data-label="Next" className="ffx-num">{s.nextRunAt ? when.format(new Date(s.nextRunAt)) : "-"}</td>
                  <td>
                    {s.canManage ? (
                      <span className="ffa-rowactions">
                        <Button size="sm" variant="ghost" loading={pause.isPending && pause.variables?.id === s.id} onClick={() => pause.mutate({ id: s.id, paused: s.state !== "paused" })} aria-label={`${s.state === "paused" ? "Resume" : "Pause"} ${s.viewName ?? "schedule"}`}>{s.state === "paused" ? "Resume" : "Pause"}</Button>
                        <Button size="sm" variant="ghost" onClick={() => void remove(s)} aria-label={`Delete schedule for ${s.viewName ?? "view"}`}>Delete</Button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog}
    </section>
  );
}

type CustomerHit = { id: string; name: string };

/** One report: saved views, filters (range, grouping, carrier/customer/queue), columns, a chart, the table, CSV. */
export function ReportPage() {
  const { reportId = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const params = paramsFromUrl(sp);
  const viewId = sp.get("view");
  const qs = apiQuery(params);
  const q = useQuery({ queryKey: ["console", "report", reportId, qs], queryFn: () => api<ReportData>(`/v1/console/reports/${reportId}${qs ? `?${qs}` : ""}`), placeholderData: keepPreviousData });
  const d = q.data;
  const [columnsOpen, setColumnsOpen] = useState(false);
  const apply = (p: ReportParams, view: string | null = viewId) => setSp(urlFromParams(p, view), { replace: true });
  const patch = (x: Partial<ReportParams>) => {
    const n: ReportParams = { ...params, ...x };
    for (const k of Object.keys(x) as (keyof ReportParams)[]) if (x[k] === undefined || x[k] === "") delete n[k];
    apply(n);
  };
  const { menu, overlays, active } = useReportViews({ reportId, params, viewId, onApply: (p, v) => apply(p, v) });

  const range: RangePreset = params.range ?? (params.from || params.to ? "custom" : "last_90_days");
  const groups = d?.groups ?? [];
  const filters = d?.filters ?? [];
  const allCols = d?.allColumns ?? d?.columns ?? [];
  const visible = new Set(d?.columns.map((c) => c.key) ?? []);
  const items: FilterItem[] = [
    {
      key: "range",
      priority: 1,
      active: range !== "last_90_days",
      inline: <Select variant="toolbar" label="Dates" value={range} active={range !== "last_90_days"} options={RANGE_PRESETS.map((r) => ({ value: r, label: RANGE_LABEL[r] }))} onChange={(v) => (v === "custom" ? patch({ range: "custom", from: d?.from, to: d?.to }) : patch({ range: v as RangePreset, from: undefined, to: undefined }))} />,
    },
    ...(range === "custom"
      ? [
          { key: "from", priority: 2, inline: <DatePicker label="From" value={params.from ?? d?.from ?? ""} onChange={(v) => patch({ range: "custom", from: v })} variant="toolbar" rule="any" cutoffHour={null} /> },
          { key: "to", priority: 2, inline: <DatePicker label="To" value={params.to ?? d?.to ?? ""} onChange={(v) => patch({ range: "custom", to: v })} variant="toolbar" rule="any" cutoffHour={null} /> },
        ]
      : []),
    ...(groups.length
      ? [{ key: "group", priority: 3, active: !!params.group && params.group !== groups[0], inline: <Select variant="toolbar" label="By" value={d?.group ?? groups[0]!} active={!!params.group && params.group !== groups[0]} options={groups.map((g) => ({ value: g, label: GROUP_LABEL[g] }))} onChange={(v) => patch({ group: v as ReportGroup })} /> }]
      : []),
    ...(filters.includes("carrier")
      ? [{ key: "carrier", priority: 4, active: !!params.carrier, inline: <Select variant="toolbar" label="Carrier" value={params.carrier ?? ""} active={!!params.carrier} options={[{ value: "", label: "Any" }, ...CARRIERS.map(([value, label]) => ({ value, label }))]} onChange={(v) => patch({ carrier: v || undefined })} /> }]
      : []),
    ...(filters.includes("queue")
      ? [{ key: "queue", priority: 4, active: !!params.queue, inline: <Select variant="toolbar" label="Queue" value={params.queue ?? ""} active={!!params.queue} options={[{ value: "", label: "Any" }, ...QUEUES.map((x) => ({ value: x, label: x[0]!.toUpperCase() + x.slice(1) }))]} onChange={(v) => patch({ queue: v || undefined })} /> }]
      : []),
    ...(filters.includes("customer")
      ? [
          {
            key: "customer",
            priority: 5,
            active: !!params.customer,
            inline: params.customer ? (
              <Button size="sm" variant="secondary" className="ffa-chipfilter" onClick={() => patch({ customer: undefined, customerName: undefined })} aria-label={`Customer: ${params.customerName ?? "selected"}. Clear`}>
                Customer: {params.customerName ?? "selected"} <span aria-hidden="true">×</span>
              </Button>
            ) : (
              <div className="ffa-customerfilter">
                <Combobox
                  label="Customer"
                  placeholder="Any customer"
                  emptyText="No customer by that name."
                  load={async (text) => ((await api<{ customers: CustomerHit[] }>(`/v1/console/customers?q=${encodeURIComponent(text)}&kind=customers`)).customers ?? []).map((c) => ({ value: c.id, label: c.name }))}
                  onSelect={(o) => patch({ customer: o.value, customerName: o.label })}
                />
              </div>
            ),
          },
        ]
      : []),
  ];
  const anyActive = items.some((i) => i.active) || !!params.cols?.length;

  const measure = d?.columns.find((c) => c.kind === "money") ?? d?.columns.find((c) => c.kind === "number");
  const label = d?.columns[0];
  const max = d && measure ? Math.max(1, ...d.rows.map((r) => Number(r[measure.key] ?? 0))) : 1;
  const csvHref = `/v1/console/reports/${reportId}?${apiQuery(params, { format: "csv" })}`;
  return (
    <section className="ffa-page">
      <Link to="/reports" className="ffa-back">← Reports</Link>
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">{active ? active.name : (d?.title ?? "Report")}</h1>
          {d ? <p className="ffa-head__sub">{active ? `${d.title}. ` : ""}{d.description}{d.showsCost ? "" : " Cost and margin are hidden for your role."}</p> : null}
        </div>
        <div className="ffa-head__actions">
          {allCols.length > 2 ? <Button onClick={() => setColumnsOpen(true)}>Columns ({visible.size}/{allCols.length})</Button> : null}
          <a className="ffx-btn" href={csvHref} download>Download CSV</a>
        </div>
      </header>
      <FilterToolbar label="Report settings" items={items} anyActive={anyActive} onReset={() => apply({}, null)} trailing={menu} meta={d ? <span className="ffa-small ffa-muted ffx-num">{d.from} to {d.to}</span> : null} />
      {active ? <SchedulesPanel viewId={active.id} compact /> : null}
      {q.isError ? <div className="ffa-notice" role="alert"><span>{message(q.error)}</span></div> : null}
      {!d ? (
        <p className="ffa-muted">Loading...</p>
      ) : d.rows.length === 0 ? (
        <EmptyState title="Nothing in this period" body="Try a wider date range or fewer filters." />
      ) : (
        <>
          {measure && label ? (
            <figure className="ffx-card ffa-card ffa-hbars" aria-label={`${measure.label} by ${label.label.toLowerCase()}`}>
              <figcaption className="ffx-micro">{measure.label} by {label.label.toLowerCase()}</figcaption>
              <ul>
                {d.rows.slice(0, 15).map((r, i) => (
                  <li key={i}>
                    <span className="ffa-hbars__label ffa-trunc" title={String(r[label.key])}>{String(r[label.key] ?? "-")}</span>
                    <span className="ffa-hbars__track" aria-hidden="true"><span className="ffa-hbars__bar" style={{ inlineSize: `${(Math.max(0, Number(r[measure.key] ?? 0)) / max) * 100}%` }} /></span>
                    <span className="ffx-num ffa-hbars__value">{fmtCell(measure, r[measure.key] ?? null)}</span>
                  </li>
                ))}
              </ul>
            </figure>
          ) : null}
          <div className="ffx-table-wrap ffa-tablewrap" aria-busy={q.isFetching}>
            <table className="ffx-table ffa-table">
              <caption className="ffx-sr">{d.title}, {d.from} to {d.to}</caption>
              <thead>
                <tr>{d.columns.map((c) => <th key={c.key} scope="col" className={c.kind === "text" ? undefined : "ffx-table__num"}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {d.rows.map((r, i) => (
                  <tr key={i}>{d.columns.map((c) => <td key={c.key} data-label={c.label} className={c.kind === "text" ? undefined : "ffx-table__num ffx-num"}>{fmtCell(c, r[c.key] ?? null)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <Dialog open={columnsOpen} onClose={() => setColumnsOpen(false)} title="Columns" description="Choose what the table, CSV and scheduled emails show. The first column always shows." footer={<><Button onClick={() => patch({ cols: undefined })}>Show all</Button><Button variant="primary" onClick={() => setColumnsOpen(false)}>Done</Button></>}>
        <ul className="ffa-colpicker">
          {allCols.map((c, i) => (
            <li key={c.key}>
              <Toggle
                label={c.label}
                checked={i === 0 || visible.has(c.key)}
                disabled={i === 0}
                onChange={(on) => {
                  const next = allCols.slice(1).map((x) => x.key).filter((k) => (k === c.key ? on : visible.has(k)));
                  patch({ cols: next.length === allCols.length - 1 ? undefined : next.length ? next : [allCols[1]!.key] });
                }}
              />
            </li>
          ))}
        </ul>
      </Dialog>
      {overlays}
    </section>
  );
}
